import {
  Icons,
  Skeleton,
  Stack,
  StatusChip,
  tokens,
  Tooltip,
} from '@cast/design-system';
import { parseClusterScore } from '@cast/features-cluster-shared';
import { dashesOrNum } from '@cast/utils';

const scoreChipSkeletonSx = {
  borderRadius: '4px',
  flexShrink: 0,
};

const getScoreChipColors = (normalized: number) => {
  if (normalized >= 7) {
    return {
      bg: tokens.theme.bg.status.successStrong,
      fg: tokens.theme.fg.status.onSuccessStrong,
    };
  }

  if (normalized >= 3) {
    return {
      bg: tokens.theme.bg.status.warningStrong,
      fg: tokens.theme.fg.status.onWarningStrong,
    };
  }

  return {
    bg: tokens.theme.bg.status.errorStrong,
    fg: tokens.theme.fg.status.onErrorStrong,
  };
};

const unknownScoreColors = {
  bg: tokens.theme.bg.status.neutralSubtle,
  fg: tokens.theme.fg.status.onNeutralSubtle,
};

type ScoreBadgeProps = {
  score?: string | number;
  isEvaluating?: boolean;
};

export const ScoreBadgeSkeleton = () => (
  <Skeleton variant="rounded" width={36} height={20} sx={scoreChipSkeletonSx} />
);

export const ScoreBadge = ({ score, isEvaluating }: ScoreBadgeProps) => {
  if (isEvaluating) {
    return (
      <Tooltip title="Initial report generation can take up to 10 minutes">
        <Stack
          data-testid="score-evaluating"
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            backgroundColor: tokens.theme.bg.status.neutralSubtle,
            borderRadius: '4px',
            py: '4px',
            px: '10px',
            width: 'max-content',
            color: tokens.theme.fg.status.onNeutralSubtle,
          }}
        >
          <Icons.HourglassMedium size={16} />
        </Stack>
      </Tooltip>
    );
  }

  if (score === '?') {
    return (
      <StatusChip
        size="sm"
        sx={{
          bgcolor: unknownScoreColors.bg,
          color: unknownScoreColors.fg,
        }}
      >
        ?
      </StatusChip>
    );
  }

  const parsed = parseClusterScore(
    typeof score === 'number' ? String(score) : score
  );

  if (parsed == null) {
    return null;
  }

  const normalized = parsed > 10 ? parsed / 10 : parsed;
  const colors = getScoreChipColors(normalized);

  return (
    <StatusChip
      size="sm"
      sx={{
        bgcolor: colors.bg,
        color: colors.fg,
      }}
    >
      {dashesOrNum({ value: normalized, roundBy: 1 })}
    </StatusChip>
  );
};
